import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Button, Col, Modal, ModalBody, ModalHeader } from "reactstrap";
import Select from "react-select";
import MaterialTableContainerWithNoFilters from "components/Common/MaterialTableContainerWithNoFilters";
import {
	getTableBodyCellProps,
	getTableHeaderCellProps,
} from "components/Common/MaterialTableStyles";
import { FloppyDisk } from "@phosphor-icons/react";
import { SendaCustomSwal } from "components/Common/SendaCustomSwal";
import userService from "services/UserService";
import organizationalStructureService from "services/OrganizationalStructureService";
import ataEncaminhamentosService from "services/AtaEncaminhamentosService";
import indicadoresService from "services/IndicadoresService";
import actionPlansService from "services/ActionPlansService";

const MigrateItensToNewDepartmentModal = ({
	isOpen,
	onClose,
	isLoading,
	setIsLoading,
	originalDepartmentId,
	newDepartmentId,
}) => {
	const customStyles = {
		option: (provided, state) => ({
			...provided,
			color: state.isSelected ? "blue" : "black",
			backgroundColor: "white",
			"&:hover": {
				backgroundColor: "#556ee6",
			},
		}),
		menuPortal: (provided) => ({
			...provided,
			zIndex: 9999,
		}),
		menu: (provided) => ({
			...provided,
			zIndex: 9999,
		}),
	};

	const [atasData, setAtasData] = useState([]);
	const [planosDeAcaoData, setPlanosDeAcaoData] = useState([]);
	const [indicadoresData, setIndicadoresData] = useState([]);
	const [departamentos, setDepartamentos] = useState([]);
	const [usuarios, setUsuarios] = useState([]);

	// Estados para os selects das atas
	const [atasDepartamento, setAtasDepartamento] = useState({});
	const [atasResponsavel, setAtasResponsavel] = useState({});

	// Estados para os selects dos planos de ação
	const [planosDeAcaoDepartamento, setPlanosDeAcaoDepartamento] = useState(
		{},
	);
	const [planosDeAcaoResponsavel, setPlanosDeAcaoResponsavel] = useState({});
	const [planosDeAcaoAprovador, setPlanosDeAcaoAprovador] = useState({});

	// Estados para os selects dos indicadores
	const [indicadoresDepartamento, setIndicadoresDepartamento] = useState({});

	const getAtasMigrations = async () => {
		try {
			const response =
				await organizationalStructureService.getAtasMigrationsByDepartment(
					originalDepartmentId,
				);
			const sortedData = response.sort((a, b) =>
				a.referrals.localeCompare(b.referrals, "pt-BR"),
			);
			setAtasData(sortedData);
		} catch (error) {
			console.log(error);
		}
	};

	const getPlanoAcaoMigrations = async () => {
		try {
			const response =
				await organizationalStructureService.getPlanoAcaoMigrationsByDepartment(
					originalDepartmentId,
				);
			const sortedData = response.sort((a, b) =>
				a.guideline.localeCompare(b.guideline, "pt-BR"),
			);
			setPlanosDeAcaoData(sortedData);
		} catch (error) {
			console.log(error);
		}
	};

	const getIndicadoresMigrations = async () => {
		try {
			const response =
				await organizationalStructureService.getIndicadoresMigrationsByDepartment(
					originalDepartmentId,
				);
			const sortedData = response.sort((a, b) =>
				a.intention_goals.localeCompare(b.intention_goals, "pt-BR"),
			);
			setIndicadoresData(sortedData);
		} catch (error) {
			console.log(error);
		}
	};

	const getUsuarios = async () => {
		try {
			const response = await userService.get();
			const options = response.map((item) => ({
				value: item.id,
				label: item.lastname,
			}));
			const sortedOptions = options.sort((a, b) =>
				a.label.localeCompare(b.label, "pt-BR"),
			);
			setUsuarios(sortedOptions);
		} catch (error) {
			console.log(error);
		}
	};

	const getDepartamentos = async () => {
		try {
			const response = await organizationalStructureService.getAll();
			const options = response.map((item) => ({
				value: item.id,
				label: item.name,
				active: item.active === "1",
				parent_id: item.parent_id,
			}));
			const sortedOptions = options.sort((a, b) =>
				a.label.localeCompare(b.label, "pt-BR"),
			);
			setDepartamentos(sortedOptions);
		} catch (error) {
			console.log(error);
		}
	};

	useEffect(() => {
		const loadData = async () => {
			if (originalDepartmentId) {
				await getUsuarios();
				await getDepartamentos();
				await getAtasMigrations();
				await getPlanoAcaoMigrations();
				await getIndicadoresMigrations();
				console.log("originalDepartmentId", originalDepartmentId);
				console.log("newDepartmentId", newDepartmentId);
			}
		};
		loadData();
	}, [originalDepartmentId]);

	// Funções de manipulação de alterações em selects das Atas
	const handleAtasDepartamentoChange = async (id, value) => {
		// Se o valor for igual ao newDepartmentId, usar ele, senão manter o original
		const targetDepartmentId =
			value === newDepartmentId ? newDepartmentId : originalDepartmentId;

		setAtasDepartamento((prev) => ({
			...prev,
			[id]: { organizational_structure_id: targetDepartmentId },
		}));

		try {
			setAtasData((prev) =>
				prev.map((row) =>
					row.id === id
						? {
								...row,
								organizational_structure_id: targetDepartmentId,
						  }
						: row,
				),
			);
		} catch (error) {
			console.log(error);
		}
	};

	const handleAtasResponsavelChange = async (id, value) => {
		setAtasResponsavel((prev) => ({
			...prev,
			[id]: { responsible_id: value },
		}));

		try {
			setAtasData((prev) =>
				prev.map((row) =>
					row.id === id ? { ...row, responsible_id: value } : row,
				),
			);
		} catch (error) {
			console.log(error);
		}
	};

	// Funções de manipulação de alterações em selects dos Planos de Ação
	const handlePlanoAcaoDepartamentoChange = async (id, value) => {
		// Se o valor for igual ao newDepartmentId, usar ele, senão manter o original
		const targetDepartmentId =
			value === newDepartmentId ? newDepartmentId : originalDepartmentId;

		setPlanosDeAcaoDepartamento((prev) => ({
			...prev,
			[id]: { organizational_structure_id: targetDepartmentId },
		}));

		try {
			setPlanosDeAcaoData((prev) =>
				prev.map((row) =>
					row.id === id
						? {
								...row,
								organizational_structure_id: targetDepartmentId,
						  }
						: row,
				),
			);
		} catch (error) {
			console.log(error);
		}
	};

	const handlePlanoAcaoResponsavelChange = async (id, value) => {
		setPlanosDeAcaoResponsavel((prev) => ({
			...prev,
			[id]: { responsible_id: value },
		}));

		try {
			setPlanosDeAcaoData((prev) =>
				prev.map((row) =>
					row.id === id ? { ...row, responsible_id: value } : row,
				),
			);
		} catch (error) {
			console.log(error);
		}
	};

	const handlePlanoAcaoAprovadorChange = async (id, value) => {
		setPlanosDeAcaoAprovador((prev) => ({
			...prev,
			[id]: { approving_id: value },
		}));

		try {
			setPlanosDeAcaoData((prev) =>
				prev.map((row) =>
					row.id === id ? { ...row, approving_id: value } : row,
				),
			);
		} catch (error) {
			console.log(error);
		}
	};

	// Funções de manipulação de alterações em selects dos Indicadores
	const handleIndicadoresDepartamentoChange = async (id, value) => {
		// Se o valor for igual ao newDepartmentId, usar ele, senão manter o original
		const targetDepartmentId =
			value === newDepartmentId ? newDepartmentId : originalDepartmentId;

		setIndicadoresDepartamento((prev) => ({
			...prev,
			[id]: { organization_id: targetDepartmentId },
		}));

		try {
			setIndicadoresData((prev) =>
				prev.map((row) =>
					row.id === id
						? { ...row, organization_id: targetDepartmentId }
						: row,
				),
			);
		} catch (error) {
			console.log(error);
		}
	};

	// Função para salvar todas as alterações
	const handleSaveChanges = async () => {
		try {
			setIsLoading(true);

			// Salvar alterações das Atas
			for (const [id, value] of Object.entries(atasDepartamento)) {
				console.log("Enviando para API - Atas Departamento:", {
					id,
					data: {
						organizational_structure_id:
							value.organizational_structure_id,
					},
				});
				await ataEncaminhamentosService.put(id, {
					organizational_structure_id:
						value.organizational_structure_id,
				});
			}
			for (const [id, value] of Object.entries(atasResponsavel)) {
				console.log("Enviando para API - Atas Responsável:", {
					id,
					data: { responsible_id: value.responsible_id },
				});
				await ataEncaminhamentosService.put(id, {
					responsible_id: value.responsible_id,
				});
			}

			// Salvar alterações dos Planos de Ação
			for (const [id, value] of Object.entries(
				planosDeAcaoDepartamento,
			)) {
				console.log("Enviando para API - Plano Ação Departamento:", {
					id,
					data: {
						organizational_structure_id:
							value.organizational_structure_id,
					},
				});
				await actionPlansService.put(id, {
					organizational_structure_id:
						value.organizational_structure_id,
				});
			}
			for (const [id, value] of Object.entries(planosDeAcaoResponsavel)) {
				console.log("Enviando para API - Plano Ação Responsável:", {
					id,
					data: { responsible_id: value.responsible_id },
				});
				await actionPlansService.put(id, {
					responsible_id: value.responsible_id,
				});
			}
			for (const [id, value] of Object.entries(planosDeAcaoAprovador)) {
				console.log("Enviando para API - Plano Ação Aprovador:", {
					id,
					data: { approving_id: value.approving_id },
				});
				await actionPlansService.put(id, {
					approving_id: value.approving_id,
				});
			}

			// Salvar alterações dos Indicadores
			for (const [id, value] of Object.entries(indicadoresDepartamento)) {
				console.log("Enviando para API - Indicador Departamento:", {
					id,
					data: { organization_id: value.organization_id },
				});
				await indicadoresService.put(id, {
					organization_id: value.organization_id,
				});
			}

			// Limpar estados após salvar
			setAtasDepartamento({});
			setAtasResponsavel({});
			setPlanosDeAcaoDepartamento({});
			setPlanosDeAcaoResponsavel({});
			setPlanosDeAcaoAprovador({});
			setIndicadoresDepartamento({});

			// Recarregar dados
			await getAtasMigrations();
			await getPlanoAcaoMigrations();
			await getIndicadoresMigrations();

			SendaCustomSwal.fire({
				iconHtml: '<i class="success-icon"></i>',
				title: "Sucesso",
				text: "Alterações salvas com sucesso!",
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
		} catch (error) {
			console.error("Erro ao salvar alterações:", error);
			SendaCustomSwal.fire({
				iconHtml: '<i class="error-icon"></i>',
				title: "Erro",
				text: "Erro ao salvar alterações. Por favor, tente novamente.",
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
		} finally {
			setIsLoading(false);
		}
	};

	// Função para verificar se há alterações pendentes
	const hasPendingChanges = () => {
		return (
			Object.keys(atasDepartamento).length > 0 ||
			Object.keys(atasResponsavel).length > 0 ||
			Object.keys(planosDeAcaoDepartamento).length > 0 ||
			Object.keys(planosDeAcaoResponsavel).length > 0 ||
			Object.keys(planosDeAcaoAprovador).length > 0 ||
			Object.keys(indicadoresDepartamento).length > 0
		);
	};

	// Colunas para a tabela de Atas com selects
	const atasColumn = [
		{
			header: "ATA",
			accessorKey: "referrals",
			enableColumnFilter: false,
			enableColumnActions: false,
			enableSorting: false,
			...getTableHeaderCellProps(),
			...getTableBodyCellProps("firstColumn"),
			size: 30,
		},
		{
			header: "DEPARTAMENTO",
			accessorKey: "organizational_structure_id",
			enableColumnFilter: false,
			enableColumnActions: false,
			enableSorting: false,
			...getTableHeaderCellProps(),
			...getTableBodyCellProps(),
			size: 30,
			Cell: ({ row }) => {
				const id = row.original.id;
				const currentValue =
					atasDepartamento[id]?.organizational_structure_id ||
					row.original.organizational_structure_id ||
					originalDepartmentId;

				// Mostrar apenas o departamento original e o novo departamento como opções
				const availableOptions = departamentos.filter(
					(dep) =>
						String(dep.value) === String(originalDepartmentId) ||
						String(dep.value) === String(newDepartmentId),
				);

				const currentDepartamento = availableOptions.find(
					(dep) => String(dep.value) === String(currentValue),
				);

				return (
					<Select
						value={currentDepartamento}
						onChange={(selectedOption) =>
							handleAtasDepartamentoChange(
								id,
								selectedOption.value,
							)
						}
						options={availableOptions}
						placeholder="Selecione..."
						isSearchable
						menuPortalTarget={document.body}
						styles={customStyles}
					/>
				);
			},
		},
		{
			header: "RESPONSÁVEL",
			accessorKey: "responsible_id",
			enableColumnFilter: false,
			enableColumnActions: false,
			enableSorting: false,
			...getTableHeaderCellProps(),
			...getTableBodyCellProps(),
			size: 30,
			Cell: ({ row }) => {
				const id = row.original.id;
				const currentValue =
					atasResponsavel[id] || row.original.responsible_id || "";

				return (
					<Select
						value={usuarios.find(
							(user) =>
								String(user.value) === String(currentValue),
						)}
						onChange={(selectedOption) =>
							handleAtasResponsavelChange(
								id,
								selectedOption ? selectedOption.value : null,
							)
						}
						options={usuarios}
						placeholder="Selecione..."
						isSearchable
						menuPortalTarget={document.body}
						styles={customStyles}
					/>
				);
			},
		},
	];

	// Colunas para a tabela de Planos de Ação com selects
	const planosDeAcaoColumn = [
		{
			header: "PLANO DE AÇÃO",
			accessorKey: "guideline",
			enableColumnFilter: false,
			enableColumnActions: false,
			enableSorting: false,
			...getTableHeaderCellProps(),
			...getTableBodyCellProps("firstColumn"),
			size: 30,
		},
		{
			header: "DEPARTAMENTO",
			accessorKey: "organizational_structure_id",
			enableColumnFilter: false,
			enableColumnActions: false,
			enableSorting: false,
			...getTableHeaderCellProps(),
			...getTableBodyCellProps(),
			size: 30,
			Cell: ({ row }) => {
				const id = row.original.id;
				const currentValue =
					planosDeAcaoDepartamento[id]?.organizational_structure_id ||
					row.original.organizational_structure_id ||
					originalDepartmentId;

				// Mostrar apenas o departamento original e o novo departamento como opções
				const availableOptions = departamentos.filter(
					(dep) =>
						String(dep.value) === String(originalDepartmentId) ||
						String(dep.value) === String(newDepartmentId),
				);

				const currentDepartamento = availableOptions.find(
					(dep) => String(dep.value) === String(currentValue),
				);

				return (
					<Select
						value={currentDepartamento}
						onChange={(selectedOption) =>
							handlePlanoAcaoDepartamentoChange(
								id,
								selectedOption ? selectedOption.value : null,
							)
						}
						options={availableOptions}
						placeholder="Selecione..."
						isSearchable
						menuPortalTarget={document.body}
						styles={customStyles}
					/>
				);
			},
		},
		{
			header: "RESPONSÁVEL",
			accessorKey: "responsible_id",
			enableColumnFilter: false,
			enableColumnActions: false,
			enableSorting: false,
			...getTableHeaderCellProps(),
			...getTableBodyCellProps(),
			size: 30,
			Cell: ({ row }) => {
				const id = row.original.id;
				const currentValue =
					planosDeAcaoResponsavel[id] ||
					row.original.responsible_id ||
					"";

				return (
					<Select
						value={usuarios.find(
							(user) =>
								String(user.value) === String(currentValue),
						)}
						onChange={(selectedOption) =>
							handlePlanoAcaoResponsavelChange(
								id,
								selectedOption ? selectedOption.value : null,
							)
						}
						options={usuarios}
						placeholder="Selecione..."
						isSearchable
						menuPortalTarget={document.body}
						styles={customStyles}
					/>
				);
			},
		},
		{
			header: "APROVADOR",
			accessorKey: "approving_id",
			enableColumnFilter: false,
			enableColumnActions: false,
			enableSorting: false,
			...getTableHeaderCellProps(),
			...getTableBodyCellProps(),
			size: 30,
			Cell: ({ row }) => {
				const id = row.original.id;
				const currentValue =
					planosDeAcaoAprovador[id] ||
					row.original.approving_id ||
					"";

				return (
					<Select
						value={usuarios.find(
							(user) =>
								String(user.value) === String(currentValue),
						)}
						onChange={(selectedOption) =>
							handlePlanoAcaoAprovadorChange(
								id,
								selectedOption ? selectedOption.value : null,
							)
						}
						options={usuarios}
						placeholder="Selecione..."
						isSearchable
						menuPortalTarget={document.body}
						styles={customStyles}
					/>
				);
			},
		},
	];

	// Colunas para a tabela de Indicadores com selects
	const indicadoresColumn = [
		{
			header: "INDICADOR",
			accessorKey: "intention_goals",
			enableColumnFilter: false,
			enableColumnActions: false,
			enableSorting: false,
			...getTableHeaderCellProps(),
			...getTableBodyCellProps("firstColumn"),
			size: 30,
		},
		{
			header: "DEPARTAMENTO",
			accessorKey: "organization_id",
			enableColumnFilter: false,
			enableColumnActions: false,
			enableSorting: false,
			...getTableHeaderCellProps(),
			...getTableBodyCellProps(),
			size: 30,
			Cell: ({ row }) => {
				const id = row.original.id;
				const currentValue =
					indicadoresDepartamento[id]?.organization_id ||
					row.original.organization_id ||
					originalDepartmentId;

				// Mostrar apenas o departamento original e o novo departamento como opções
				const availableOptions = departamentos.filter(
					(dep) =>
						String(dep.value) === String(originalDepartmentId) ||
						String(dep.value) === String(newDepartmentId),
				);

				const currentDepartamento = availableOptions.find(
					(dep) => String(dep.value) === String(currentValue),
				);

				return (
					<Select
						value={currentDepartamento}
						onChange={(selectedOption) =>
							handleIndicadoresDepartamentoChange(
								id,
								selectedOption ? selectedOption.value : null,
							)
						}
						options={availableOptions}
						placeholder="Selecione..."
						isSearchable
						menuPortalTarget={document.body}
						styles={customStyles}
					/>
				);
			},
		},
	];

	return (
		<Modal isOpen={isOpen} toggle={onClose} size="lg" centered={true}>
			<ModalHeader toggle={onClose}>
				Migração de itens para novo departamento
			</ModalHeader>
			<ModalBody>
				<Col style={{ padding: "20px" }}>
					{atasData && atasData.length > 0 && (
						<div style={{ marginBottom: "30px" }}>
							<h3>Atas</h3>
							<MaterialTableContainerWithNoFilters
								columns={atasColumn}
								data={atasData}
							/>
						</div>
					)}

					{planosDeAcaoData && planosDeAcaoData.length > 0 && (
						<div style={{ marginBottom: "30px" }}>
							<h3>Planos de Ação</h3>
							<MaterialTableContainerWithNoFilters
								columns={planosDeAcaoColumn}
								data={planosDeAcaoData}
							/>
						</div>
					)}

					{indicadoresData && indicadoresData.length > 0 && (
						<div style={{ marginBottom: "30px" }}>
							<h3>Indicadores</h3>
							<MaterialTableContainerWithNoFilters
								columns={indicadoresColumn}
								data={indicadoresData}
							/>
						</div>
					)}

					{(!atasData || atasData.length === 0) &&
						(!planosDeAcaoData || planosDeAcaoData.length === 0) &&
						(!indicadoresData || indicadoresData.length === 0) && (
							<h2
								style={{
									textAlign: "center",
									marginTop: "50px",
								}}
							>
								Não existe nenhuma migração a ser feita.
							</h2>
						)}

					{hasPendingChanges() && (
						<div
							style={{
								display: "flex",
								justifyContent: "start",
								marginTop: 20,
							}}
						>
							<Button
								onClick={handleSaveChanges}
								style={{
									width: 200,
									background: "none",
									padding: 10,
									cursor: "pointer",
									color: "black",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									gap: 3,
								}}
								disabled={isLoading}
							>
								<FloppyDisk size={16} /> Salvar Mudanças
							</Button>
						</div>
					)}
				</Col>
			</ModalBody>
		</Modal>
	);
};

MigrateItensToNewDepartmentModal.propTypes = {
	isOpen: PropTypes.bool,
	onClose: PropTypes.func,
	isLoading: PropTypes.bool,
	setIsLoading: PropTypes.func,
	originalDepartmentId: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.number,
	]).isRequired,
	newDepartmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
		.isRequired,
};

export default MigrateItensToNewDepartmentModal;
